import _defaultsDeep from 'lodash/defaultsDeep';


import { getService } from '@homeservices/api-kit/servicecatalog/services';
import { getServiceAvailability } from '@homeservices/api-kit/servicecatalog/availability';
import { createWorkOrder, updateWorkOrder } from '@homeservices/api-kit/workorder/proreferral/v2';
import { persist, load, clear } from '@/utils/state-sync';

import { URGENCIES } from '@/constants/urgencies';
import deviceDetect from '@/plugins/device/device-detection';
import isZipcodeChosen from '@/utils/is-zipcode-chosen';

import details from './modules/details';
import matches from './modules/matches';
import overview from './modules/overview';

// eslint-disable-next-line camelcase
async function checkAvailability ({ zipcode, service_identifier }) {
  const { data } = await getServiceAvailability({ zipcode, service_identifier });
  return data.count > 0;
}

function initialState () {
  return {
    service: {},
    location: {},
    isServiceAvailable: false,
    // TODO: Convert (ref)workOrderID  to (ref)workOrderId
    workOrderID: null,
    refWorkOrderID: null,
    sourceID: null,
  };
}

export const JOB_REQUEST_LOCAL_STORAGE_NAME = 'JobRequestData';

export const JobRequestPlugins = [
  persist({
    name: JOB_REQUEST_LOCAL_STORAGE_NAME,
    keys: [
      'jobrequest.details.notes',
      'jobrequest.details.selectedUrgencyId',
      'jobrequest.details.uploadedImages',
      'jobrequest.matches.selectedPublicProfilesIds',
      'jobrequest.matches.touchedPublicProfilesIds',
      'jobrequest.overview.email',
      'jobrequest.overview.firstName',
      'jobrequest.overview.lastName',
      'jobrequest.overview.phone',
      'jobrequest.overview.smsOptIn',
    ],
  }),
];

export default {
  namespaced: true,
  modules: {
    details,
    matches,
    overview,
  },
  state: initialState,
  getters: {
    service (state) {
      return state.service;
    },
    location (state) {
      return state.location;
    },
    sourceID (state) {
      return state.sourceID;
    },
    isCityChosen (state) {
      return Boolean(state.location.city);
    },
    isStateChosen (state) {
      return Boolean(state.location.state);
    },
    isServiceAvailable (state) {
      return state.isServiceAvailable;
    },
  },
  actions: {
    async fetchAvailability ({ commit, state }, { zipcode }) {
      if (!isZipcodeChosen(zipcode)) {
        return;
      }

      const isServiceAvailable = await checkAvailability({
        zipcode,
        service_identifier: state.service.service_identifier,
      });

      commit('setIsServiceAvailable', isServiceAvailable);
    },
    async fetchService ({ commit }, serviceId) {
      const { data } = await getService(serviceId);
      commit('setService', data);
    },
    async submit ({ state, rootState }, { zipcode, sms }) {
      const { timeZone } = global.Intl.DateTimeFormat().resolvedOptions();
      const recaptchaState = rootState.recaptcha || {};
      const uid = recaptchaState.userInteractionId || null;

      const body = {
        time_zone: timeZone,
        interaction_id: uid,
        service_identifier: state.service.service_identifier,
        source: {
          channel: 'proreferral',
          device_type: deviceDetect.isMobile ? 'mobile_web' : 'web',
          click_source: state.sourceID,
        },
        store: {
          store_number: '',
          associate_id: '',
        },
        urgency: URGENCIES[state.details.selectedUrgencyId].key,
        description: state.details.notes,
        assets_urls: state.details.uploadedImages.map(({ url }) => url),
        user: {
          user_id: rootState.user.me.id,
          terms_service_acceptance: true,
        },
        contact: {
          first_name: state.overview.firstName,
          last_name: state.overview.lastName,
          email: state.overview.email,
          phone: state.overview.phone,
          city: state.location.city,
          state: state.location.state,
          zipcode,
        },
        pro_match: {
          pro_match_request_id: state.matches.proMatchRequestId,
          service_provider_occupation_ids: state.matches.selectedPublicProfilesIds,
        },
        sms_opt_in: sms,
      };

      let response;
      if (state.workOrderID) {
        body.order_identifier = state.workOrderID;
        body.store.store_number = state.HDStore;
        body.store.associate_id = state.associate_id;
        response = await updateWorkOrder(body);
      } else if (body.contact.email && body.user.user_id) {
        response = await createWorkOrder(body);
      }

      return response;
    },
    clear ({ commit, dispatch }) {
      commit('clear');
      dispatch('details/clear');
      dispatch('matches/clear');
      dispatch('overview/clear');
    },
    // added new update action to handle new 9900 flow but left old flow in place to handle existing 9900 leads ussing old url from email
    async update ({ state, rootState }, { zipcode }) {
      const { timeZone } = global.Intl.DateTimeFormat().resolvedOptions();
      const body = {
        time_zone: timeZone,
        service_identifier: state.service.service_identifier,
        source: {
          channel: 'service_connect',
          device_type: deviceDetect.isMobile ? 'mobile_web' : 'web',
          click_source: state.sourceID,
        },
        store: {
          store_number: '',
          associate_id: '',
        },
        urgency: 'undecided',
        description: state.details.notes,
        assets_urls: state.details.uploadedImages.map(({ url }) => url),
        user: {
          user_id: rootState.user.me.id,
          terms_service_acceptance: true,
        },
        contact: {
          email: state.overview.email,
          first_name: state.overview.firstName,
          last_name: state.overview.lastName,
          phone: state.overview.phone,
          city: state.location.city,
          state: state.location.state,
          zipcode,
        },
        pro_match: {
          pro_match_request_id: state.matches.proMatchRequestId,
          service_provider_occupation_ids: state.matches.selectedPublicProfilesIds,
        },
        sms_opt_in: null,
      };

      body.order_identifier = state.workOrderID;
      body.store.store_number = state.HDStore;
      body.store.associate_id = state.associate_id;

      const response = await updateWorkOrder(body);
      return response;
    },
  },
  mutations: {
    setIsServiceAvailable (state, value) {
      state.isServiceAvailable = value;
    },
    setService (state, service) {
      state.service = service;
    },
    setLocation (state, { city, state: _state }) {
      state.location = { city, state: _state };
    },
    setRefWorkOrderId (state, value) {
      state.refWorkOrderID = value;
    },
    setSourceID (state, value) {
      state.sourceID = value;
    },
    setHDStore (state, value) {
      state.HDStore = value;
    },
    setAssociateId (state, value) {
      state.associate_id = value;
    },
    setWorkOrderId (state, value) {
      state.workOrderID = value;
    },
    load (state) {
      const localState = load({ name: JOB_REQUEST_LOCAL_STORAGE_NAME });
      const updatedState = _defaultsDeep(localState.jobrequest, state);
      Object.assign(state, updatedState);
    },
    clear (state) {
      Object.assign(state, initialState());
      clear({ name: JOB_REQUEST_LOCAL_STORAGE_NAME });
    },
  },
};
